import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Send, Lock, Bell, Circle, Banknote, ChevronDown, ChevronUp } from 'lucide-react';
import { invoiceApi } from '../lib/api';
import { formatRelativeTime } from '@dutchinvoice/shared-utils';
import { useInvoiceStore } from '../stores/invoiceStore';
import { useAuthStore } from '../stores/authStore';
import { calculateInvoiceTotals } from '../utils/invoiceCalculations';
import { useMoneyDisplay } from '../utils/money';
import { useTranslation } from '../hooks/useTranslation';

interface InvoiceNonEditableOverlayProps {
  status: string;
  paymentStatus: string;
}

interface EmailHistoryItem {
  id: string;
  invoice_id: string;
  user_id: string;
  recipient_email: string;
  status: 'planned' | 'sent' | 'failed';
  scheduled_at: string;
  sent_at?: string | null;
  attempts: number;
  email_locale?: string;
  custom_message?: string | null;
  error?: string | null;
}

export const InvoiceNonEditableOverlay: React.FC<InvoiceNonEditableOverlayProps> = ({
  status,
  paymentStatus
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sentDate, setSentDate] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const currentInvoice = useInvoiceStore((state) => state.currentInvoice);
  const { isAuthenticated } = useAuthStore();
  const { formatCurrency } = useMoneyDisplay();
  const { t, locale: interfaceLanguage } = useTranslation();

  const isNonEditable = status === 'sent' || paymentStatus === 'paid' || paymentStatus === 'overdue' || paymentStatus === 'overdue_reminded';

  // Calculate invoice total
  const { total } = calculateInvoiceTotals(currentInvoice.items, currentInvoice.discount);

  // Add overflow hidden to parent invoice container during animations
  React.useEffect(() => {
    const invoiceContainer = document.querySelector('.invoice-content');
    if (invoiceContainer && isAnimating) {
      invoiceContainer.classList.add('overflow-hidden');
      return () => {
        invoiceContainer.classList.remove('overflow-hidden');
      };
    }
  }, [isAnimating]);

  // Add overflow hidden to invoice-container-inner when overlay is shown
  React.useEffect(() => {
    const invoiceContainerInner = document.querySelector('.invoice-container-inner');
    if (invoiceContainerInner && isNonEditable) {
      invoiceContainerInner.classList.add('overflow-hidden');
      return () => {
        invoiceContainerInner.classList.remove('overflow-hidden');
      };
    }
  }, [isNonEditable]);

  // Fetch email history to get actual send date
  useEffect(() => {
    if (status === 'sent' && currentInvoice.id && isAuthenticated) {
      const fetchSentDate = async () => {
        try {
          const emailResponse = await invoiceApi.getInvoiceEmails(currentInvoice.id!);
          
          // First try to find a sent email with sent_at
          const sentEmail = emailResponse.emails?.find((email: EmailHistoryItem) => 
            email.status === 'sent' && email.sent_at
          );
          
          if (sentEmail?.sent_at) {
            setSentDate(sentEmail.sent_at);
          } else {
            // If no sent email found, look for planned/queued emails and use scheduled_at
            const queuedEmail = emailResponse.emails?.find((email: EmailHistoryItem) => 
              email.status === 'planned' && email.scheduled_at
            );
            
            if (queuedEmail?.scheduled_at) {
              setSentDate(queuedEmail.scheduled_at);
            }
          }
        } catch (error) {
          console.error('Failed to fetch email history:', error);
          // Fallback to invoice date if email history fails
          setSentDate(currentInvoice.date);
        }
      };
      fetchSentDate();
    }
  }, [status, currentInvoice.id, currentInvoice.date, isAuthenticated]);

  // Get send status info
  const getSendStatus = () => {
    if (status !== 'sent') return null;
    
    const displayDate = sentDate ? formatRelativeTime(sentDate, interfaceLanguage) : t('overlay.recently');
    const emailAddress = currentInvoice.recipient.email;
    
    return {
      label: t('emailHistory.sent'),
      icon: Send,
      color: '#3B82F6',
      message: emailAddress ? `${displayDate} • ${t('emailHistory.to', { email: emailAddress })}` : `${displayDate} • ${t('overlay.invoiceSent')}`,
      shortMessage: t('invoice.sentNonEditableMessage')
    };
  };

  // Get payment status info
  const getPaymentStatus = () => {
    if (paymentStatus === 'paid') {
      const paidDate = currentInvoice.paidAt ? formatRelativeTime(currentInvoice.paidAt, interfaceLanguage) : t('overlay.recently');
      
      // Check if there's a paid payment link to get the provider
      const paidPaymentLink = currentInvoice.paymentLinks?.find(link => link.status === 'paid');
      const provider = paidPaymentLink ? 
        paidPaymentLink.provider.charAt(0).toUpperCase() + paidPaymentLink.provider.slice(1) : 
        null;
      
      return {
        label: t('payment.status.markedPaid'),
        icon: Banknote,
        color: '#10B981',
        message: provider ? `${paidDate} • ${t('invoice.history.paymentReceivedFromProvider', { provider })}` : `${paidDate} • ${t('overlay.paymentReceived')}`
      };
    }
    if (paymentStatus === 'overdue') {
      return {
        label: t('payment.status.overdue'),
        icon: AlertCircle,
        color: '#EF4444',
        message: t('payment.status.overdue')
      };
    }
    if (paymentStatus === 'overdue_reminded') {
      const reminderDate = currentInvoice.lastReminderSentAt
        ? formatRelativeTime(currentInvoice.lastReminderSentAt, interfaceLanguage)
        : t('overlay.recently');

      return {
        label: t('payment.status.overdue'),
        icon: Bell,
        color: '#F59E0B', // Amber color for reminded state (more friendly than pure red)
        message: t('payment.status.reminderSent', { date: reminderDate })
      };
    }
    if (paymentStatus === 'unpaid' && status === 'sent') {
      return {
        label: t('payment.status.unpaid'),
        icon: Circle,
        color: '#6B7280',
        message: t('payment.status.unpaid')
      };
    }
    return null;
  };

  const sendStatusInfo = getSendStatus();
  const paymentStatusInfo = getPaymentStatus();

  if (!isNonEditable || (!sendStatusInfo && !paymentStatusInfo)) return null;

  // Determine if invoice should be blurred (only when expanded)
  const shouldBlur = !isCollapsed;

  const handleToggleCollapse = (newCollapsedState: boolean) => {
    setIsAnimating(true);
    setIsCollapsed(newCollapsedState);
    // Clear animation state after animation completes
    setTimeout(() => setIsAnimating(false), 250); // Even faster fade-in
  };

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 pointer-events-none z-10"
      >
        {/* Elegant blur overlay */}
        <motion.div 
          className="absolute inset-0 rounded-2xl"
          animate={{
            backdropFilter: shouldBlur ? 'blur(6px)' : 'blur(0px)',
            backgroundColor: shouldBlur ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0)'
          }}
          transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
        />
        
        {/* Control panel - morphing between expanded and collapsed states */}
        <div className={`absolute bottom-8 left-0 right-0 flex justify-center pointer-events-auto ${isCollapsed ? 'items-end' : ''}`}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ 
              opacity: 1, 
              scale: 1, 
              y: 0
            }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 25,
              delay: 0.1 
            }}
            layout
            className={`bg-white/95 relative z-50 backdrop-blur-xl shadow-xl border border-white/50 overflow-hidden ${
              isCollapsed 
                ? 'rounded-t-2xl shadow-[0_4px_25px_15px_rgba(0,0,0,0.15),0_-4px_10px_-6px_rgba(0,0,0,0.1)]' 
                : 'rounded-2xl'
            }`}
            style={{
              marginBottom: isCollapsed ? '-40px' : '0'
            }}
          >
            {isCollapsed ? (
              // Collapsed state - attached to bottom
              <motion.div
                layoutId="panel-content"
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors duration-200"
                initial={false}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                onClick={() => handleToggleCollapse(false)}
              >
                <div
                  className="flex items-center gap-3"
                  style={{ 
                    opacity: isAnimating ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                  }}
                >
                  <Lock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600 font-medium whitespace-nowrap">
                    {t('overlay.readOnly')}
                  </span>
                  <button
                    onClick={() => handleToggleCollapse(false)}
                    className="btn-secondary w-9 h-9 p-0 flex items-center justify-center ml-3"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            ) : (
              // Expanded state - full panel
              <motion.div
                layoutId="panel-content"
                className="p-6"
                initial={false}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                style={{ minWidth: '320px', maxWidth: '400px' }}
              >
                {/* Header with invoice info */}
                <motion.div 
                  className="mb-6"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  {/* Top row: Invoice info (left) and minimize button (right) */}
                  <div className="flex justify-between items-start mb-4">
                    {/* Invoice header - left side */}
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.3, delay: 0.15 }}
                    >
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {currentInvoice.invoiceNumber}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{currentInvoice.recipient.company}</span>
                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                        <span className="font-medium">{formatCurrency(total)}</span>
                      </div>
                    </motion.div>

                    {/* Minimize button - right side */}
                    <motion.button
                      onClick={() => handleToggleCollapse(true)}
                      className="btn-secondary w-9 h-9 p-0 flex items-center justify-center"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.2, delay: 0.1 }}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </motion.button>
                  </div>
                </motion.div>

                {/* Send Status */}
                {sendStatusInfo && (
                  <motion.div 
                    className="flex items-center gap-4 mb-4"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${sendStatusInfo.color}15` }}
                    >
                      <sendStatusInfo.icon 
                        className="h-5 w-5" 
                        style={{ color: sendStatusInfo.color }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-base font-semibold text-gray-900">
                          {sendStatusInfo.label}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        {sendStatusInfo.message}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Payment Status */}
                {paymentStatusInfo && (
                  <motion.div 
                    className="flex items-center gap-4 mb-6"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3, delay: paymentStatusInfo && sendStatusInfo ? 0.25 : 0.2 }}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${paymentStatusInfo.color}15` }}
                    >
                      <paymentStatusInfo.icon 
                        className="h-5 w-5" 
                        style={{ color: paymentStatusInfo.color }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-base font-semibold text-gray-900">
                          {paymentStatusInfo.label}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        {paymentStatusInfo.message}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Lock indicator */}
                <motion.div 
                  className="flex items-center gap-3 py-3 px-4 bg-gray-50 rounded-xl border border-gray-100"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                >
                  <Lock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600 font-medium">
                    {sendStatusInfo?.shortMessage || t('overlay.editingDisabled')}
                  </span>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Interaction blocker when expanded */}
        {shouldBlur && (
          <div 
            className="absolute inset-0 cursor-pointer pointer-events-auto" 
            onClick={() => handleToggleCollapse(true)}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
};